import { useMemo, useState, useSyncExternalStore } from 'react'
import type { JSX } from 'react'

import { Button, Combobox, Text } from '@buffer-mono/popcorn'

import { TagSelectorButton } from '~publish/components/Composer/components/TagSelector'

import { TagEditDialogForm } from '~publish/components/TagEditDialog'
import { TagsComboboxView } from '~publish/components/TagsCombobox/TagsComboboxView'
import type { GuestDraftStore } from '~publish/helpers/guestDrafts'
import { GUEST_TAG_LIMIT, normalizeGuestTags } from '~publish/helpers/guestTags'
import type { GuestTag } from '~publish/helpers/guestTags'

import styles from './PublicTagSelector.module.css'

export function PublicTagSelector({
  store,
  selectedTags,
  onChange,
}: {
  store: GuestDraftStore
  selectedTags: readonly GuestTag[]
  onChange: (tags: readonly GuestTag[]) => void
}): JSX.Element {
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot)
  const [createdTags, setCreatedTags] = useState<readonly GuestTag[]>([])
  const [open, setOpen] = useState(false)
  const [newTag, setNewTag] = useState<{ name: string }>()
  const usedTagNames = useMemo(
    () =>
      new Set(
        [
          ...snapshot.drafts.flatMap((draft) => draft.tags ?? []),
          ...selectedTags,
        ].map((tag) => tag.name.toLowerCase()),
      ),
    [snapshot, selectedTags],
  )
  const limitReached = usedTagNames.size >= GUEST_TAG_LIMIT
  const tags = useMemo(() => {
    const byName = new Map<string, GuestTag>()
    for (const tag of [
      ...snapshot.drafts.flatMap((draft) => draft.tags ?? []),
      ...createdTags,
      ...selectedTags,
    ]) {
      const key = tag.name.toLowerCase()
      if (limitReached && !usedTagNames.has(key)) continue
      byName.set(key, tag)
    }
    return Array.from(byName.values()).toSorted((a, b) =>
      a.name.localeCompare(b.name),
    )
  }, [snapshot, createdTags, selectedTags, limitReached, usedTagNames])
  const selectedNames = selectedTags.map((tag) => tag.name.toLowerCase())

  const createTag = (name: string): void => {
    if (limitReached) return
    setOpen(false)
    setNewTag({ name })
  }

  return (
    <div className={styles.root}>
      <TagsComboboxView
        tags={tags.map((tag) => ({
          id: tag.name.toLowerCase(),
          name: tag.name,
          color: tag.color,
        }))}
        open={open}
        onOpenChange={setOpen}
        selectedTags={selectedNames}
        onChange={(names): void => {
          onChange(tags.filter((tag) => names.includes(tag.name.toLowerCase())))
        }}
        limit={GUEST_TAG_LIMIT}
        canCreate={!limitReached}
        onCreate={createTag}
        createFromSearchWithoutTags
        footer={
          <Combobox.Footer>
            {limitReached ? (
              <Text size="sm">
                Up to {GUEST_TAG_LIMIT} tags across your drafts
              </Text>
            ) : (
              <Button variant="tertiary" onClick={(): void => createTag('')}>
                New tag
              </Button>
            )}
            <Combobox.Clear>
              <Button variant="tertiary">Clear All</Button>
            </Combobox.Clear>
          </Combobox.Footer>
        }
        align="start"
        className={styles.content}
      >
        <TagSelectorButton
          tags={selectedTags.map((tag) => ({
            key: tag.name.toLowerCase(),
            name: tag.name,
            color: tag.color,
          }))}
          maxVisibleTags={2}
        />
      </TagsComboboxView>
      <TagEditDialogForm
        open={newTag !== undefined}
        tag={newTag}
        existingTags={tags}
        onOpenChange={(isOpen): void => {
          if (!isOpen) setNewTag(undefined)
        }}
        onSubmit={(value): void => {
          const [normalized] = normalizeGuestTags([
            {
              name: value.name,
              color: value.color,
            },
          ])
          if (!normalized || limitReached) return
          const tag =
            tags.find(
              (existing) =>
                existing.name.toLowerCase() === normalized.name.toLowerCase(),
            ) ?? normalized
          setCreatedTags((current) => [...current, tag])
          onChange(normalizeGuestTags([...selectedTags, tag]))
        }}
      />
    </div>
  )
}
